<template>
	<div class="qualified-weekly-leaderboard">
		<v-container fluid v-if="loading" key="loading-container">
			<LeaderBoardRowSkeleton />
		</v-container>
		<div v-else key="leaderboard-content">
			<h2 id="WeeklyQualifiedTitle" class="ortho-title text-center pb-5 animate__animated animate__fadeInDown">
				{{ qualifiedTitle }}
			</h2>

			<!-- Ajout du bouton de switch -->
			<div class="switch-container mb-4 animate__animated animate__fadeIn">
				<div class="cursor-pointer" @click="toggleUserScope">
					<span class="ortho-bold fs12 ortho-text-gray cta" style="color: rgba(0, 0, 0, 0.54)">{{
						showAllUsers ? 'VOIR MA LIGUE' : 'VOIR TOUS LES UTILISATEURS'
					}}</span>
					<v-icon class="mr3 arrow-right" color="#979FA9" small>mdi-arrow-down</v-icon>
				</div>
				<InfoTooltip position="left">
					<span>
						Le classement se base sur les points obtenus lors des sessions validées cette semaine.
						<br />Plus vous validez de sessions, plus vous gagnez de points !
					</span>
				</InfoTooltip>
			</div>

			<div class="mb-3 divider animate__animated animate__fadeIn animate__delay-1s"></div>

			<div id="LeaderboardTable">
				<transition-group
					name="staggered-fade"
					tag="div"
					@before-enter="beforeEnter"
					@enter="enter"
					@after-enter="afterEnter"
				>
					<template v-for="(item, index) in visibleLeaderboard">
						<MyPositionRow
							:key="`${item.formationId}-me`"
							v-if="item != null && item.formationId === myFormationId"
							:name="capitalizeName(item.userName)"
							:score="item.totalPoints"
							:position="item.position"
							:class="{ 'first-place-animation': item.position === 1 }"
							:data-index="index"
						/>
						<OtherUserRow
							:key="`${item.formationId}-other`"
							v-else
							:name="capitalizeName(item.userName)"
							:score="item.totalPoints"
							:position="item.position"
							:class="{ 'first-place-animation': item.position === 1 }"
							:data-index="index"
						/>
					</template>
				</transition-group>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import LeaderBoardRowSkeleton from '@/components/skeletons/LeaderBoardRowSkeleton.vue';
import OtherUserRow from './OtherUserRow';
import MyPositionRow from './MyPositionRow';
import InfoTooltip from '@/components/tooltip/InfoTooltip.vue';
import { capitalize } from '@/utils/text.js';

export default {
	name: 'QualifiedWeeklyLeaderBoard',
	components: { LeaderBoardRowSkeleton, OtherUserRow, MyPositionRow, InfoTooltip },
	props: {
		myFormationId: String,
	},
	data() {
		return {
			loading: true,
			leaderboard: null,
			visibleLeaderboard: [],
			showAllUsers: false,
			soundFirstPlace: null,
		};
	},
	computed: {
		...mapGetters('leaderboard', {
			weeklyLeaderboard: 'weeklyLeaderboard',
		}),

		qualifiedTitle() {
			const { position } = this.leaderboard.find((item) => item.formationId === this.myFormationId);
			if (position === 1) return 'Vous êtes classé 1er cette semaine, félicitations';
			if (position === 2) return 'Vous êtes classé 2ème cette semaine, félicitations';
			if (position === 3) return 'Vous êtes classé 3ème cette semaine, félicitations';
			return `Vous êtes classé ${position}ème cette semaine`;
		},
	},
	async created() {
		await this.fetchWeeklyLeaderboard();
		// Précharger le son pour la première place
		this.soundFirstPlace = new Audio(require('@/assets/sound/next-parcours-unlocked.mp3'));
	},

	methods: {
		dataExpired(timeString) {
			if (timeString == null) return true;
			const threeMinutesAgo = new Date();
			threeMinutesAgo.setMinutes(threeMinutesAgo.getMinutes() - 3);
			return new Date(timeString) < threeMinutesAgo;
		},
		async toggleUserScope() {
			try {
				this.loading = true;
				this.showAllUsers = !this.showAllUsers;
				await this.fetchWeeklyLeaderboard();
			} catch (error) {
				// Handle error silently
			} finally {
				this.loading = false;
			}
		},
		async fetchWeeklyLeaderboard() {
			try {
				if (this.myFormationId == null) return;
				const leaderboardData = await this.$store.dispatch('leaderboard/fetchWeeklyLeaderboard', {
					formationId: this.myFormationId,
					showAllUsers: this.showAllUsers,
				});
				this.leaderboard = leaderboardData;

				// Réinitialiser le tableau visible
				this.visibleLeaderboard = [];

				// Commencer l'animation après un court délai
				setTimeout(() => {
					this.animateLeaderboard();
				}, 500);
			} catch (error) {
				// Handle error silently
			} finally {
				this.loading = false;
			}
		},
		capitalizeName(name) {
			return capitalize(name);
		},
		// Méthodes pour l'animation
		beforeEnter(el) {
			el.style.opacity = 0;
			el.style.transform = 'translateY(30px)';
		},
		enter(el, done) {
			const delay = el.dataset.index * 150; // 150ms de délai entre chaque élément
			setTimeout(() => {
				el.style.transition = 'all 0.5s ease';
				el.style.opacity = 1;
				el.style.transform = 'translateY(0)';

				// Jouer un son pour le premier utilisateur
				if (el.classList.contains('first-place-animation') && this.soundFirstPlace) {
					this.soundFirstPlace.play();
				}

				done();
			}, delay);
		},
		afterEnter(el) {
			// Ajouter une animation spéciale pour la première place
			if (el.classList.contains('first-place-animation')) {
				el.classList.add('animate__animated', 'animate__pulse');
			}
		},
		animateLeaderboard() {
			// Ajouter progressivement chaque utilisateur au tableau visible
			const addNextUser = (index) => {
				if (index < this.leaderboard.length) {
					this.visibleLeaderboard.push(this.leaderboard[index]);
					setTimeout(() => addNextUser(index + 1), 150);
				}
			};

			// Commencer l'animation
			addNextUser(0);
		},
	},
};
</script>

<style lang="scss" scoped>
.qualified-weekly-leaderboard {
	.divider {
		background-color: #eee;
		height: 2px;
	}
}

.switch-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.switch-container .cursor-pointer {
	display: flex;
	justify-content: flex-start;
	align-items: center;
}

#LeaderboardTable {
	padding-top: 10px;
}

.scope-toggle-btn {
	font-size: 0.875rem;
	text-transform: none;
	letter-spacing: normal;
}

// Animation pour la première place
.first-place-animation {
	position: relative;
}

// Animation d'entrée pour chaque ligne
.staggered-fade-enter-active {
	transition: all 0.5s ease;
}

.staggered-fade-leave-active {
	transition: all 0.3s ease;
}

.staggered-fade-enter,
.staggered-fade-leave-to {
	opacity: 0;
	transform: translateY(30px);
}

@media screen and (max-width: 960px) {
	#LeaderboardTable {
		padding-right: 10px;
		padding-left: 10px;
	}
	#WeeklyQualifiedTitle {
		font-size: 20px;
	}
}
</style>
